import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Linkedin, Mail, Check } from 'lucide-react';

/**
 * One contact in a referral path, plus the small pieces it is built from.
 *
 * WHY THIS IS SHARED
 *
 * The job detail page and the Hiring Signals (US) company page both show the
 * same thing — a person, their title, their email and their LinkedIn — and both
 * get it from the same endpoint. Rendering it twice would let the two drift, so
 * the job page imports these rather than keeping its own copies.
 *
 * What is deliberately NOT here: the ranking, the tier badges, the caveats and
 * the approach advice. Those belong to the contacts modal, where there is room
 * to explain them. This card is a preview.
 */

export const avatarBg = (name) => {
    const p = ['#6366F1', '#8B5CF6', '#059669', '#D97706', '#EC4899', '#3F57E0', '#0D9488', '#DC2626'];
    return p[(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % p.length];
};

export const initials = (name) => {
    if (!name) return '?';
    const p = name.trim().split(' ');
    return (p.length >= 2 ? p[0][0] + p[p.length - 1][0] : name[0]).toUpperCase();
};

export const contactEmail = (contact) => {
    const rawEmail = contact.email || contact.work_email || contact.personal_email
        || contact._fullData?.work_email || contact._fullData?.email || null;
    return typeof rawEmail === 'string' && rawEmail.includes('@') ? rawEmail : null;
};

// Tooltip rendered through a body portal with fixed positioning, so it never
// gets clipped by overflow containers (e.g. the contacts modal scroll area).
// Flips below the anchor when there's no room above.
export const HoverTip = ({ tip, children, className = '' }) => {
    const [anchor, setAnchor] = useState(null);
    const clampX = (x) => Math.min(Math.max(x, 118), window.innerWidth - 118);
    return (
        <div className={className}
             onMouseEnter={e => setAnchor(e.currentTarget.getBoundingClientRect())}
             onMouseLeave={() => setAnchor(null)}>
            {children}
            {anchor && createPortal(
                <div className="fixed z-[10001] pointer-events-none bg-[#0f172a] text-white text-[11px] rounded-lg px-2.5 py-1.5 w-max max-w-[220px] -translate-x-1/2"
                     style={anchor.top > 80
                         ? { bottom: window.innerHeight - anchor.top + 6, left: clampX(anchor.left + anchor.width / 2) }
                         : { top: anchor.bottom + 6, left: clampX(anchor.left + anchor.width / 2) }}>
                    {tip}
                </div>,
                document.body
            )}
        </div>
    );
};

// Click-to-copy email icon with hover tooltip (shared by sidebar cards + modal).
// Contacts without an email get a greyed-out icon so rows stay visually aligned.
export const EmailCopyButton = ({ email }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
        e.stopPropagation();
        if (!email) return;
        navigator.clipboard.writeText(email);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <HoverTip className="shrink-0"
                  tip={email
                      ? <><p className="truncate">{email}</p><p className="text-gray-400 text-[10px] mt-0.5">Click to copy</p></>
                      : <p className="text-gray-300">No email found</p>}>
            <button onClick={handleCopy}
                    className={`p-1.5 rounded-lg transition-colors flex ${
                        !email ? 'text-gray-300 cursor-default'
                        : copied ? 'text-[#10B981]'
                        : 'text-gray-400 hover:text-[#3F57E0]'}`}>
                {copied ? <Check className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}
            </button>
        </HoverTip>
    );
};

// Normalized referral likelihood (accepts 0–1 or 0–100) + badge colors.
export const referralProb = (contact) => {
    if (contact.referral_probability == null) return null;
    const n = Number(contact.referral_probability);
    const prob = Math.round(n >= 1 ? n : n * 100);
    return {
        prob,
        color: prob >= 70 ? '#10B981' : prob >= 50 ? '#3F57E0' : '#F59E0B',
        bg:    prob >= 70 ? '#f0fdf4' : prob >= 50 ? '#EEF1FE' : '#fffbeb',
    };
};

export const REFERRAL_PROB_TIP = (
    <>
        <p className="font-semibold">Referral likelihood</p>
        <p className="text-gray-300 mt-0.5 leading-snug">How likely this contact is to respond and refer you, based on their role and seniority.</p>
    </>
);

const ContactCard = ({ contact, onSelect }) => {
    const photo = contact.profile_photo || contact.photo_url || contact.profile_picture || contact.picture || contact.avatar || contact._fullData?.profile_photo || contact._fullData?.photo_url || null;
    const email = contactEmail(contact);

    return (
        <div className="flex items-center gap-3 bg-[#F8F9FF] rounded-xl p-3">
            <div className="w-10 h-10 rounded-full shrink-0 overflow-hidden border-2 border-white shadow-sm">
                {photo ? (
                    <img src={photo} alt={contact.name}
                         className="w-full h-full object-cover"
                         onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                ) : null}
                <div className="w-full h-full rounded-full flex items-center justify-center text-white text-[13px] font-bold"
                     style={{ background: `linear-gradient(135deg, ${avatarBg(contact.name)}, ${avatarBg(contact.name)}cc)`, display: photo ? 'none' : 'flex' }}>
                    {initials(contact.name)}
                </div>
            </div>
            <div className="flex-1 min-w-0 text-left">
                <p className="text-[13px] font-semibold text-[#0f172a] truncate">{contact.name || 'Unknown'}</p>
                <p className="text-[11.5px] text-gray-400 truncate">{contact.title || ''}</p>
            </div>
            <EmailCopyButton email={email} />
            {contact.profile_url ? (
                <a href={contact.profile_url} target="_blank" rel="noopener noreferrer"
                   onClick={e => e.stopPropagation()}
                   className="p-1.5 rounded-lg text-gray-400 hover:text-[#0A66C2] transition-colors shrink-0">
                    <Linkedin className="w-3.5 h-3.5" />
                </a>
            ) : (
                <HoverTip className="shrink-0" tip={<p className="text-gray-300">No LinkedIn profile found</p>}>
                    <span className="p-1.5 flex text-gray-300 cursor-default">
                        <Linkedin className="w-3.5 h-3.5" />
                    </span>
                </HoverTip>
            )}
        </div>
    );
};

export default ContactCard;
